import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "upr_cart_v1";

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn("[cart] failed to read localStorage:", e);
    return [];
  }
}

function persist(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    console.warn("[cart] failed to persist (likely quota):", e);
  }
}

// A line item is uniquely identified by (merch_id, variant_label).
const lineKey = (merchId, variantLabel) => `${merchId}::${variantLabel || ""}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(load);
  const [open, setOpen] = useState(false);

  useEffect(() => { persist(items); }, [items]);

  const add = useCallback((merch, variantLabel, qty = 1) => {
    setItems((prev) => {
      const k = lineKey(merch.id, variantLabel);
      const idx = prev.findIndex((l) => lineKey(l.merch_id, l.variant_label) === k);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: Math.min(10, next[idx].quantity + qty) };
        return next;
      }
      return [...prev, {
        merch_id: merch.id,
        merch_name: merch.name,
        image_url: merch.image_url,
        price: Number(merch.price) || 0,
        currency: merch.currency || "EUR",
        category: merch.category || "",
        variant_label: variantLabel || "",
        quantity: Math.min(10, Math.max(1, qty)),
        has_wav: !!merch.release_id,
      }];
    });
    setOpen(true);
  }, []);

  const setQuantity = useCallback((merchId, variantLabel, qty) => {
    setItems((prev) => prev.map((l) => {
      if (lineKey(l.merch_id, l.variant_label) === lineKey(merchId, variantLabel)) {
        return { ...l, quantity: Math.min(10, Math.max(1, qty)) };
      }
      return l;
    }));
  }, []);

  const remove = useCallback((merchId, variantLabel) => {
    setItems((prev) => prev.filter((l) => lineKey(l.merch_id, l.variant_label) !== lineKey(merchId, variantLabel)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((s, l) => s + l.quantity, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, l) => s + l.quantity * l.price, 0), [items]);
  const currency = items[0]?.currency || "EUR";

  const value = useMemo(() => ({
    items, count, subtotal, currency,
    open, setOpen,
    add, setQuantity, remove, clear,
  }), [items, count, subtotal, currency, open, add, setQuantity, remove, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
